import { useCallback, useEffect, useRef, useState } from "react";

export type AsyncState<T> =
	| { status: "loading"; data: null; error: null }
	| { status: "ready"; data: T; error: null }
	| { status: "error"; data: null; error: unknown };

/**
 * 読み取り用の最小Data Fetch。Libraryを前提にしない（design §24.5）。
 *
 * 失敗を空Dataへ丸めない。呼び出し側は必ず`status`で分岐する。
 */
export function useAsync<T>(
	load: () => Promise<T>,
	deps: ReadonlyArray<unknown>,
): AsyncState<T> & { reload(): void } {
	const [state, setState] = useState<AsyncState<T>>({ status: "loading", data: null, error: null });
	const [nonce, setNonce] = useState(0);
	const loadRef = useRef(load);
	loadRef.current = load;

	// biome-ignore lint/correctness/useExhaustiveDependencies: depsは呼び出し側が明示する契約
	useEffect(() => {
		let cancelled = false;
		setState({ status: "loading", data: null, error: null });
		loadRef
			.current()
			.then((data) => {
				if (!cancelled) setState({ status: "ready", data, error: null });
			})
			.catch((error: unknown) => {
				if (!cancelled) setState({ status: "error", data: null, error });
			});
		return () => {
			cancelled = true;
		};
	}, [...deps, nonce]);

	const reload = useCallback(() => setNonce((n) => n + 1), []);
	return { ...state, reload };
}

/** 書き込み操作の状態。成功したことにしないため、errorを必ず表に出す。 */
export function useAction<TArgs extends unknown[], TResult>(
	action: (...args: TArgs) => Promise<TResult>,
) {
	const [pending, setPending] = useState(false);
	const [error, setError] = useState<unknown>(null);
	const [result, setResult] = useState<TResult | null>(null);

	const run = useCallback(
		async (...args: TArgs): Promise<TResult | null> => {
			setPending(true);
			setError(null);
			try {
				const value = await action(...args);
				setResult(value);
				return value;
			} catch (caught) {
				setError(caught);
				return null;
			} finally {
				setPending(false);
			}
		},
		[action],
	);

	return { run, pending, error, result, clearError: useCallback(() => setError(null), []) };
}

/** 冪等キー。同じ操作の二重送信で副作用を二重確定させない。 */
export function useIdempotencyKey(prefix: string): { key: string; rotate(): void } {
	const [seed, setSeed] = useState(() => Math.random().toString(36).slice(2, 10));
	return {
		key: `${prefix}-${seed}`,
		rotate: useCallback(() => setSeed(Math.random().toString(36).slice(2, 10)), []),
	};
}
